import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  BookOpen,
  ChevronRight,
  Eye,
  ThumbsUp,
  PlusCircle,
  X,
  Play,
  Edit,
  Trash2,
} from "lucide-react";
import styles from "./SeriesView.module.css";
import { useSeriesById } from "../../../hooks/admin/useAdminSeries";
import Loading from "../../../common/HousGlass Loader/Loading";
import AddAudioChapter from "./AddAudioChapter";
import AddShortsChapter from "./AddShortsChapter";
import AddEbookChapter from "./AddEbookChapter";
import { useChaptersBySeriesId, useDeleteChapter } from "../../../hooks/admin/useAdminChapters";
import DeleteChapterDialog from "./components/DeleteChapterDialog";
import AddVideoChapter from "./AddVideosChapter";

const SeriesView = () => {
  const { id } = useParams();
  const { series, isLoading } = useSeriesById(id);
  const { chaptersData, refetch: refetchChapters } = useChaptersBySeriesId(id);
  const { deleteChapter, isLoading: isDeleting } = useDeleteChapter();
  
  const [showAddChapter, setShowAddChapter] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [chapterToDelete, setChapterToDelete] = useState(null);

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Loading />
      </div>
    );
  }
  
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "published":
        return styles.published;
      case "draft":
        return styles.draft;
      case "archived":
        return styles.archived;
      default:
        return "";
    }
  };

  const renderChapterForm = () => {
    switch (series?.series_type) {
      case "Ebook":
        return <AddEbookChapter data={series} />;
      case "Audio":
        return <AddAudioChapter data={series} />;
      case "Shorts":
        return <AddShortsChapter data={series} />;
      case "Videos":
        return <AddVideoChapter data={series} />;
      default:
        return <p>Unsupported series type.</p>;
    }
  };

  const formatCount = (count) => {
    if (!count) return "0";
    return parseInt(count).toLocaleString() || "0";
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "0:00";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const toggleAddChapter = () => {
    setShowAddChapter(!showAddChapter);
  };

  const handleEditChapter = (chapterId) => {
    // Implement edit functionality
    console.log("Edit chapter:", chapterId);
  };

  const handleDeleteChapter = (chapter) => {
    setChapterToDelete(chapter);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteChapter = () => {
    if (chapterToDelete) {
      deleteChapter(chapterToDelete.chapter_id, {
        onSuccess: () => {
          setDeleteDialogOpen(false);
          setChapterToDelete(null);
          refetchChapters(); // Refresh the chapters list after deletion
        }
      });
    }
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setChapterToDelete(null);
  };

  const handlePlayChapter = (audioUrl) => {
    // Implement play functionality
    console.log("Play audio:", audioUrl);
  };

  return (
    <div className={styles.container}>
      <section className={styles.heroSection}>
        {series?.cover_image_url && (
          <img
            src={series.cover_image_url}
            alt={series.title}
            className={styles.heroBg}
          />
        )}
        <div className={styles.heroContent}>
          <div className={styles.heroInfo}>
            <div className={styles.breadcrumb}>
              <Link to="/admin/series" className={styles.breadcrumbLink}>
                <BookOpen size={16} />
                Series
              </Link>
              <ChevronRight size={16} />
              <span>View Series</span>
            </div>
            <h1 className={styles.title}>
              {series?.title || "Untitled Series"}
            </h1>
            <div className={styles.metaInfo}>
              <span className={styles.metaItem}>
                <Eye size={18} />
                {formatCount(series?.views_count)} views
              </span>
              <span className={styles.metaItem}>
                <ThumbsUp size={18} />
                {formatCount(series?.likes_count)} likes
              </span>
              <span
                className={`${styles.statusBadge} ${getStatusBadgeClass(
                  series?.status
                )}`}
              >
                {series?.status || "Unknown Status"}
              </span>
            </div>
          </div>
        </div>
      </section>

      <div className={styles.mainContent}>
        <div>
          <section className={styles.contentSection}>
            <h2 className={styles.sectionTitle}>About This Series</h2>
            <p className={styles.description}>
              {series?.description || "No description available."}
            </p>

            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <div className={styles.infoLabel}>Series Type</div>
                <div className={styles.infoValue}>
                  {series?.series_type || "Not specified"}
                </div>
              </div>
              <div className={styles.infoItem}>
                <div className={styles.infoLabel}>Language</div>
                <div className={styles.infoValue}>
                  {series?.language || "Not specified"}
                </div>
              </div>
              <div className={styles.infoItem}>
                <div className={styles.infoLabel}>Content Rating</div>
                <div className={styles.infoValue}>
                  {series?.content_rating || "Not specified"}
                </div>
              </div>
              <div className={styles.infoItem}>
                <div className={styles.infoLabel}>Visibility</div>
                <div className={styles.infoValue}>
                  {series?.visibility || "Not specified"}
                </div>
              </div>
            </div>
          </section>

          <section className={styles.contentSection}>
            <h2 className={styles.sectionTitle}>Additional Information</h2>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <div className={styles.infoLabel}>Prerequisites</div>
                <div className={styles.infoValue}>
                  {series?.prerequisites || "None"}
                </div>
              </div>
              <div className={styles.infoItem}>
                <div className={styles.infoLabel}>Target Audience</div>
                <div className={styles.infoValue}>
                  {series?.target_audience || "Not specified"}
                </div>
              </div>
            </div>
          </section>

          {/* Chapters Section */}
          <section className={styles.contentSection}>
            <h2 className={styles.sectionTitle}>Chapters</h2>
            {chaptersData && chaptersData.length > 0 ? (
              <div className={styles.chaptersList}>
                {chaptersData.map((chapter) => (
                  <div key={chapter.chapter_id} className={styles.chapterCard}>
                    <div className={styles.chapterImageContainer}>
                      <img
                        src={
                          chapter.mobile_image_url || chapter.computer_image_url
                        }
                        alt={chapter.audio_title}
                        className={styles.chapterImage}
                      />
                      {chapter.type === "audio" && (
                        <button
                          className={styles.playButton}
                          onClick={() => handlePlayChapter(chapter.audio_url)}
                        >
                          <Play size={24} />
                        </button>
                      )}
                    </div>
                    <div className={styles.chapterInfo}>
                      <div className={styles.chapterHeader}>
                        <h3 className={styles.chapterTitle}>
                          {chapter.chapter_number}.{" "}
                          {chapter.content_title ||
                            chapter.ebook_title ||
                            "Untitled Chapter"}
                        </h3>
                        <div className={styles.chapterControls}>
                          <button
                            className={styles.iconButton}
                            onClick={() =>
                              handleEditChapter(chapter.chapter_id)
                            }
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            className={styles.iconButton}
                            onClick={() => handleDeleteChapter(chapter)}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                      <p className={styles.chapterDescription}>
                        {chapter.content_description ||
                          chapter.ebook_description ||
                          "No description available."}
                      </p>
                      {chapter.duration && (
                        <div className={styles.chapterMeta}>
                          <span>
                            Duration: {formatDuration(chapter.duration)}
                          </span>
                        </div>
                      )}
                      <div className={styles.chapterStats}>
                        <span className={styles.metaItem}>
                          <Eye size={16} />
                          {formatCount(chapter.views_count)} views
                        </span>
                        <span className={styles.metaItem}>
                          <ThumbsUp size={16} />
                          {formatCount(chapter.likes_count)} likes
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className={styles.emptyState}>
                No chapters available. Add chapters to complete your series.
              </p>
            )}
          </section>
        </div>

        <aside className={styles.sidebar}>
          {series?.cover_image_url && (
            <img
              src={series.cover_image_url}
              alt={series.title}
              className={styles.coverImage}
            />
          )}

          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>SEO Information</h3>
            <div className={styles.infoItem}>
              <div className={styles.infoLabel}>SEO Title</div>
              <div className={styles.infoValue}>
                {series?.seo_title || "Not specified"}
              </div>
            </div>
            <div className={styles.infoItem}>
              <div className={styles.infoLabel}>Meta Keywords</div>
              <div className={styles.infoValue}>
                {series?.meta_keywords || "Not specified"}
              </div>
            </div>
          </div>
          <button
            onClick={toggleAddChapter}
            className={styles.addChapterButton}
          >
            <PlusCircle size={48} />
            <span>Add New Chapter</span>
          </button>
        </aside>
      </div>

      {showAddChapter && (
        <div className={styles.fullScreenOverlay}>
          <div className={styles.overlayContent}>
            <div className={styles.overlayHeader}>
              <h2>Add New Chapter</h2>
              <button onClick={toggleAddChapter} className={styles.closeButton}>
                <X size={24} />
              </button>
            </div>
            <div className={styles.overlayBody}>{renderChapterForm()}</div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteChapterDialog 
        isOpen={deleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={confirmDeleteChapter}
        chapterTitle={chapterToDelete?.content_title || chapterToDelete?.ebook_title || "this chapter"}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default SeriesView;